import { useEffect, useState } from 'react';
import { Package, Users, MessageSquare, Eye } from 'lucide-react';
import { API_URL } from '../config';

const Dashboard = () => {
  const [statsData, setStatsData] = useState({
    products: 0,
    team: 0,
    enquiries: 0,
    recentEnquiries: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    
    fetch(`${API_URL}/api/public/admin/stats`, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : ''
      }
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch stats');
        return res.json();
      })
      .then(data => {
        if (data && typeof data === 'object') {
          setStatsData({
            products: data.products || 0,
            team: data.team || 0,
            enquiries: data.enquiries || 0,
            recentEnquiries: Array.isArray(data.recentEnquiries) ? data.recentEnquiries : []
          });
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch stats', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="p-8 text-center text-slate-500">Loading dashboard...</div>;
  if (error) return <div className="p-8 text-center text-red-500">{error}</div>;

  const stats = [
    { label: 'Total Products', value: statsData.products, icon: <Package size={24} />, color: 'bg-blue-500' },
    { label: 'Team Members', value: statsData.team, icon: <Users size={24} />, color: 'bg-green-500' },
    { label: 'New Enquiries', value: statsData.enquiries, icon: <MessageSquare size={24} />, color: 'bg-orange-500' },
    { label: 'Page Views', value: '1.2k', icon: <Eye size={24} />, color: 'bg-purple-500' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Dashboard Overview</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-slate-100 flex items-center">
            <div className={`${stat.color} p-4 rounded-lg text-white mr-4`}>
              {stat.icon}
            </div>
            <div>
              <p className="text-slate-500 text-sm font-medium">{stat.label}</p>
              <p className="text-2xl font-bold text-slate-900">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Recent Enquiries</h2>
          <div className="space-y-4">
            {statsData.recentEnquiries.length > 0 ? (
              statsData.recentEnquiries.map((enquiry: any) => (
                <div key={enquiry.id} className="flex items-start border-b border-slate-50 pb-4 last:border-0 last:pb-0">
                  <div className="h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 mr-3 flex-shrink-0">
                    <MessageSquare size={16} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">New enquiry from {enquiry.name}</p>
                    <p className="text-sm text-slate-500">{enquiry.interest}</p>
                    <p className="text-xs text-slate-400 mt-1">{new Date(enquiry.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-slate-500 text-sm">No recent enquiries.</p>
            )}
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-100">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-4">
            <button className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors">
              <span className="block font-medium text-slate-900 mb-1">Add Product</span>
              <span className="text-sm text-slate-500">Create a new product listing</span>
            </button>
            <button className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors">
              <span className="block font-medium text-slate-900 mb-1">Upload Media</span>
              <span className="text-sm text-slate-500">Add images to library</span>
            </button>
            <button className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors">
              <span className="block font-medium text-slate-900 mb-1">Update Team</span>
              <span className="text-sm text-slate-500">Manage team members</span>
            </button>
            <button className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 text-left transition-colors">
              <span className="block font-medium text-slate-900 mb-1">Settings</span>
              <span className="text-sm text-slate-500">Configure site settings</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
